

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getallTask, getAllTasksForTableView, updateTask, deleteTask } from '../../../utils/API';
import { TaskState, Task, UpdateTaskPayload, FetchTasksParams } from '../types/task';
import { getStatusFilter } from '../../../utils/statusFilter';
import { useContext } from 'react';
import { AuthContext } from '../../../Auth/AuthContext';

const initialState: TaskState = {
  tasks: [],
  filteredTasks: [],
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 5,
    total: 0,
  },
  developerPaginations: {},
  tasksByDeveloper: {},   // 👈 add this
};

const currentUser = () => {
  const data = localStorage.getItem("authData");
  if (!data) return null;

  const parsed = JSON.parse(data);
  return {
    ...parsed.user,
    role: parsed.roleId?.name?.toLowerCase() || '', // injects "admin", "developer", etc.
  };
};

// Async thunks
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (params: FetchTasksParams, { rejectWithValue }) => {
    try {
      const {
        isGroupBy = true,
        page = 1,
        developerId = null,
        deadlineDate = null,   // 👈 can be string OR {startDate, endDate}
        statusFilter = getStatusFilter(),
        searchText = ""
      } = params;

      const { role, _id: userId } = currentUser();
      const developerIdToUse = developerId || (role === 'admin' ? null : userId);
      const isAdmin = role === 'admin';

      const filterPayload: any = {
        status: statusFilter,
        deadlineDate: deadlineDate ?? undefined, // 👈 backend handles normal vs custom
      };

      const response = isGroupBy && isAdmin && !params.taskId
        ? await getallTask(searchText, page, undefined, role, true, developerIdToUse, {
          ...filterPayload,
          page,
          limit: 5,
        })
        : await getAllTasksForTableView(searchText, page, developerIdToUse, role, false, {
          ...filterPayload,
          ids: params.taskId,
        });

      // console.log("Fetched tasks response:", response);
      return response;

    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const updateTaskStatus = createAsyncThunk(
  'tasks/updateTaskStatus',
  async ({ taskId, updates }: { taskId: string; updates: any }, { rejectWithValue }) => {
    try {
      const response = await updateTask(taskId, updates); // your API already handles FormData
      return { taskId, updates, response: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);




export const deleteTaskById = createAsyncThunk(
  'tasks/deleteTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      await deleteTask(taskId, true);
      return taskId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setFilteredTasks: (state, action: PayloadAction<Task[]>) => {
      state.filteredTasks = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    reorderTasks: (state, action: PayloadAction<{ sourceIndex: number; destinationIndex: number }>) => {
      const { sourceIndex, destinationIndex } = action.payload;
      const items = Array.from(state.tasks);
      const [reorderedItem] = items.splice(sourceIndex, 1);
      items.splice(destinationIndex, 0, reorderedItem);
      state.tasks = items;
      state.filteredTasks = items;
    },
    updatePagination: (state, action: PayloadAction<{ current: number; pageSize: number; total: number }>) => {
      state.pagination = action.payload;
    },
    setPagination: (state, action: PayloadAction<{ current: number; pageSize: number; total: number }>) => {
      state.pagination = action.payload;
    },
    updatePaginationCurrent: (state, action: PayloadAction<number>) => {
      state.pagination.current = action.payload;
    },
    setDeveloperPagination: (state, action: PayloadAction<{ developerId: string; pagination: { current: number; pageSize: number; total: number } }>) => {
      const { developerId, pagination } = action.payload;
      state.developerPaginations[developerId] = pagination;
    },
    updateDeveloperPaginationCurrent: (state, action: PayloadAction<{ developerId: string; current: number }>) => {
      const { developerId, current } = action.payload;
      if (state.developerPaginations[developerId]) {
        state.developerPaginations[developerId].current = current;
      }
    },
    addTaskLocal: (state, action: PayloadAction<any>) => {
      const newTask = action.payload;

      // Avoid duplicates
      if (!state.tasks.find(task => task._id === newTask._id)) {
        state.tasks = [newTask, ...state.tasks];
        state.filteredTasks = [newTask, ...state.filteredTasks];

        // ✅ Increment global total
        state.pagination.total += 1;

        // ✅ Increment developer-specific pagination if task has assignees
        if (newTask.assignees && newTask.assignees.length > 0) {
          const developerId = newTask.assignees[0]._id; // assuming first assignee
          if (developerId) {
            if (state.developerPaginations[developerId]) {
              state.developerPaginations[developerId] = {
                ...state.developerPaginations[developerId],
                total: state.developerPaginations[developerId].total + 1,
              };
            } else {
              // if developer pagination doesn't exist yet, initialize
              state.developerPaginations[developerId] = {
                current: 1,
                pageSize: 5,
                total: 1,
              };
            }
          }
        }
      }
    },
    // In your taskSlice.ts
    updateTaskLocal: (state, action) => {
      const { taskId, updates } = action.payload;

      // Update tasksByDeveloper
      if (state.tasksByDeveloper) {
        Object.keys(state.tasksByDeveloper).forEach(devId => {
          const taskIndex = state.tasksByDeveloper[devId].findIndex((task: any) => task._id === taskId);
          if (taskIndex !== -1) {
            // Remove from current developer if assignees changed
            if (updates.assignees) {
              const shouldRemove = !updates.assignees.some((a: any) => a._id === devId);
              if (shouldRemove) {
                state.tasksByDeveloper[devId] = state.tasksByDeveloper[devId].filter((task: any) => task._id !== taskId);
                return;
              }
            }

            // Update the task
            state.tasksByDeveloper[devId][taskIndex] = {
              ...state.tasksByDeveloper[devId][taskIndex],
              ...updates
            };
          } else if (updates.assignees) {
            // Add to new developer if assignees changed and task belongs here
            const shouldAdd = updates.assignees.some((a: any) => a._id === devId);
            if (shouldAdd) {
              state.tasksByDeveloper[devId] = [
                ...(state.tasksByDeveloper[devId] || []),
                { _id: taskId, ...updates }
              ];
            }
          }
        });
      }

      // Update filteredTasks
      const filteredTaskIndex = state.filteredTasks.findIndex(task => task._id === taskId);
      if (filteredTaskIndex !== -1) {
        state.filteredTasks[filteredTaskIndex] = {
          ...state.filteredTasks[filteredTaskIndex],
          ...updates
        };
      }
    },
    // In taskSlice.tsx add a reducer for local delete
    removeTaskLocal: (
      state,
      action: PayloadAction<{ taskId: string; developerId?: string }>
    ) => {
      const { taskId, developerId } = action.payload;

      // Remove from tasks + filteredTasks
      state.tasks = state.tasks.filter(task => task._id !== taskId);
      state.filteredTasks = state.filteredTasks.filter(task => task._id !== taskId);

      // Find developerId dynamically if not provided
      let devIdToUpdate = developerId;
      if (!devIdToUpdate) {
        for (const devId of Object.keys(state.tasksByDeveloper)) {
          if (state.tasksByDeveloper[devId].some((task: any) => task._id === taskId)) {
            devIdToUpdate = devId;
            break;
          }
        }
      }

      // Remove from tasksByDeveloper
      Object.keys(state.tasksByDeveloper).forEach((devId) => {
        state.tasksByDeveloper[devId] = state.tasksByDeveloper[devId].filter(
          (task: any) => task._id !== taskId
        );
      });

      // Decrement developer-specific pagination
      if (devIdToUpdate && state.developerPaginations[devIdToUpdate]) {
        state.developerPaginations[devIdToUpdate] = {
          ...state.developerPaginations[devIdToUpdate],
          total: Math.max(0, state.developerPaginations[devIdToUpdate].total - 1),
        };
      }

      // Decrement global pagination total
      if (state.pagination.total > 0) {
        state.pagination.total -= 1;
      }
    },
    updateDeveloperTasks: (state, action) => {
      const { developerId, tasks } = action.payload;
      state.tasksByDeveloper[developerId] = tasks;
    }


  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("Fetched tasks payload:", action.meta, action.payload);
        if (action.meta.arg.isGroupBy) {
          const groupedData = action.payload.data || [];

          // Store tasks by developer
          const tasksByDeveloper: Record<string, Task[]> = {};

          groupedData.forEach((devGroup: any) => {
            // ✅ Safety check: Use fallback if developer is undefined
            const devId = devGroup?.developer?._id || 'unassigned';
            
            // Skip if no tasks in this group
            if (!devGroup.tasks || devGroup.tasks.length === 0) {
              return;
            }

            tasksByDeveloper[devId] = devGroup.tasks.map((task: any) => ({
              ...task,
              developerId: devId,
            }));

            state.developerPaginations[devId] = {
              current: devGroup.taskPagination?.page || 1,
              pageSize: devGroup.taskPagination?.limit || 5,
              total: devGroup.taskPagination?.total || 0,
            };
          });

          state.tasksByDeveloper = tasksByDeveloper;   // 👈 new state field
        }
        else {
          // Handle flat response
          const tasks = action.payload.data || [];
          state.tasks = tasks;
          state.filteredTasks = tasks;
          state.pagination = {
            current: action.payload.page || 1,
            pageSize: action.payload.limit || 10,
            total: action.payload.total || 0,
          };
        }
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update task status
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        const { taskId, updates } = action.payload;

        // Update in tasks array
        const taskIndex = state.tasks.findIndex(task => task._id === taskId);
        if (taskIndex !== -1) {
          state.tasks[taskIndex] = { ...state.tasks[taskIndex], ...updates };
        }

        // Update in filteredTasks array
        const filteredIndex = state.filteredTasks.findIndex(task => task._id === taskId);
        if (filteredIndex !== -1) {
          state.filteredTasks[filteredIndex] = { ...state.filteredTasks[filteredIndex], ...updates };
        }
      })
      // Delete task
      .addCase(deleteTaskById.fulfilled, (state, action) => {
        const taskId = action.payload;

        // Try to find the developerId of the deleted task
        let devIdToUpdate: string | null = null;
        for (const devId of Object.keys(state.tasksByDeveloper)) {
          if (state.tasksByDeveloper[devId].some((task: any) => task._id === taskId)) {
            devIdToUpdate = devId;
            break;
          }
        }

        // Remove task from tasks and filteredTasks
        state.tasks = state.tasks.filter(task => task._id !== taskId);
        state.filteredTasks = state.filteredTasks.filter(task => task._id !== taskId);

        // Remove from tasksByDeveloper
        Object.keys(state.tasksByDeveloper).forEach(devId => {
          state.tasksByDeveloper[devId] = state.tasksByDeveloper[devId].filter(
            (task: any) => task._id !== taskId
          );
        });

        // Decrement correct developer pagination
        if (devIdToUpdate && state.developerPaginations[devIdToUpdate]) {
          state.developerPaginations[devIdToUpdate].total = Math.max(
            0,
            state.developerPaginations[devIdToUpdate].total - 1
          );
        }

        // Decrement global total
        if (state.pagination.total > 0) {
          state.pagination.total -= 1;
        }
      });

  },
});

export const {
  setFilteredTasks,
  clearError,
  reorderTasks,
  setPagination,
  updatePaginationCurrent,
  setDeveloperPagination,
  updateDeveloperPaginationCurrent,
  updateTaskLocal,
  removeTaskLocal,
  addTaskLocal,  // 👈 export this
  updateDeveloperTasks
} = taskSlice.actions;

export default taskSlice.reducer;
